"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import { elbow, type ConnectorLine } from "@/lib/utils/capabilityWeb.utils";

/**
 * Measures the elbow connectors that link the CapabilityWeb hub to each card.
 *
 * Geometry is read straight from the live rects (via getBoundingClientRect), so
 * the section reveals its cards with an opacity-only variant — a translate-in
 * entrance would leave the lines pointing at where the cards were mid-animation.
 * The first half of the cards (top row) connect from their BOTTOM edge up to the
 * hub; the rest (bottom row) connect from their TOP edge down.
 */
export const useConnectorLines = count => {
  const $ = _c(10);
  const stageRef = useRef(null);
  const hubRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const cardRefs = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [lines, setLines] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      w: 0,
      h: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [size, setSize] = useState(t2);
  let t3;
  let t4;
  if ($[3] !== count) {
    t3 = () => {
      const stage = stageRef.current;
      const hub = hubRef.current;
      if (!stage || !hub) {
        return;
      }
      const topRow = Math.ceil(count / 2);
      const compute = () => {
        const s = stage.getBoundingClientRect();
        const h = hub.getBoundingClientRect();
        const hx = h.left + h.width / 2 - s.left;
        const hy = h.top + h.height / 2 - s.top;
        const next = [];
        cardRefs.current.forEach((c, i) => {
          if (!c) {
            return;
          }
          const r = c.getBoundingClientRect();
          const cx = r.left + r.width / 2 - s.left;
          const cy = (i < topRow ? r.bottom : r.top) - s.top;
          next.push({
            d: elbow(hx, hy, cx, cy)
          });
        });
        setSize({
          w: s.width,
          h: s.height
        });
        setLines(next);
      };
      compute();
      const ro = new ResizeObserver(compute);
      ro.observe(stage);
      return () => ro.disconnect();
    };
    t4 = [count];
    $[3] = count;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = i_0 => el => {
      cardRefs.current[i_0] = el;
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const setCardRef = t5;
  let t6;
  if ($[7] !== lines || $[8] !== size) {
    t6 = {
      stageRef,
      hubRef,
      setCardRef,
      lines,
      size
    };
    $[7] = lines;
    $[8] = size;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};